/**
 * Centralized avatar utility functions
 * Provides consistent initials generation and color assignment across components
 */

// Curated palette: soft, muted tones that work well as avatar backgrounds
const AVATAR_COLORS = [
  // Soft Blues & Teals
  '#5B8FA8', '#6B9DAD', '#7BA3B4', '#4A90A4', '#5C9EAD', '#6AABB5', '#4F86A0', '#7CAFC4',
  // Muted Purples & Lavenders
  '#8E7DBE', '#9B8EC4', '#7A6BA0', '#A898C8', '#8B7CA8', '#9684B8', '#7E6E9F', '#A090B8',
  // Soft Pinks & Roses
  '#C49EAC', '#B8929E', '#D4A5B0', '#C8A0AA', '#BE8F9D', '#D1A8B2', '#C295A3', '#B88A98',
  // Earthy Tones
  '#A89078', '#B09882', '#9C8B7A', '#C4A484', '#A08070', '#B8A090', '#8C9B88', '#9AA894',
  // Warm Neutrals & Accents
  '#C4956A', '#B08968', '#A88B6C', '#C9A87C', '#9A8878', '#A89888', '#8A9A98', '#98A8A4',
];

/**
 * Get a consistent color for an entity based on a string identifier (ID, name, etc.)
 * Uses a simple hash to deterministically map identifiers to colors
 */
export function getAvatarColor(identifier: string): string {
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    const char = identifier.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

/**
 * Get initials from a full name string (e.g., "John Doe" -> "JD")
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === '') return '?';
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Get initials from separate first/last name fields
 * Falls back to email initial if no name is available
 */
export function getInitialsFromParts(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback?: string,
): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  const initials = (f + l).toUpperCase();
  if (initials) return initials;
  if (fallback) return fallback.charAt(0).toUpperCase();
  return '?';
}
